import React, {useEffect, useState} from 'react';
import SortableTable from 'SF/table/SortableTable';
import { Table } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import moment from 'moment';
import 'moment-timezone';
import AccessibleDropdown from 'common/AccessibleDropdown';
import css from './css/StudentSurveyListTable.scss';
import {caseInsensitiveSort} from 'utils/text';

const headers = [
    {
        content: 'Title',
        sortValue: 'title',
        width: 4
    }, {
        content: 'Description',
        sortValue: 'description',
        width: 6
    }, {
        content: 'Open Date',
        sortValue: 'startDateTime',
        width: 2
    }, {
        content: 'Close Date',
        sortValue: 'endDateTime',
        width: 2
    }, {
        content: 'Status',
        sortValue: 'status',
        width: 1
    }, {
        content: 'Enabled?',
        sortValue: 'enabled',
        width: 1
    }, {
        content: 'Actions',
        width: 2
    }
];

const sortSurveys = (surveyList, col, dir) => {
    let sortedSurvey = [];
    if(col.includes("DateTime")) {
        sortedSurvey = sortBy(surveyList, (survey) => moment(survey[col] || "3000-01-01T00:00:00Z").valueOf());
    } else {
        // For columns like title and description we don't care about case sensitivity when sorting
        sortedSurvey = caseInsensitiveSort(surveyList, col);
    }
    return dir === 'ASC' ? sortedSurvey : sortedSurvey.reverse();
};

// Option to change status available to a survey based on its current status.
// For example, a survey in Scheduled status has the option to 'Pause' the survey.
const changeStatusOption = (surveyStatus) => {
    switch (surveyStatus) {
        case 'Draft':
        case 'Closed':
            return 'Pause'
        case 'Open':
        case 'Scheduled':
            return 'Pause'
        case 'Paused':
            return 'Unpause'
    }
}

const surveyOptions = (surveyStatus) => [
    {
        key: 'key_1',
        as: 'a',
        value: 'edit',
        text: 'Edit'
    },
    {
        key: 'key_2',
        as: 'a',
        value: 'changeStatus',
        text: changeStatusOption(surveyStatus),
        disabled: surveyStatus.toUpperCase() === 'DRAFT' || surveyStatus.toUpperCase() === 'CLOSED'
    },
    {
        key: 'key_3',
        as: 'a',
        value: 'duplicate',
        text: 'Duplicate'
    }
]

const StudentSurveyListTable = ({changeSurveyStatus, duplicateTemplate, editSurvey, surveys, tenantDateFormat}) => {
    const [sortedSurveys, setSortedSurveys] = useState([]);
    const [sorting, setSorting] = useState({sortCol: 'title', dir: 'ASC'});

    useEffect(() => {
        setSortedSurveys(sortSurveys(surveys, sorting.sortCol, sorting.dir));
    }, [surveys]);

    const onSort = (col, dir) => {
        setSortedSurveys(sortSurveys(sortedSurveys, col, dir));
        setSorting({sortCol: col, dir: dir});
    }

    const renderRow = (survey, index) => (
        <Table.Row key={index} data-testid={`surveyRow-${index}`}>
            <Table.Cell>{survey.title}</Table.Cell>
            <Table.Cell>{survey.description}</Table.Cell>
            <Table.Cell>{survey.startDateTime && moment(survey.startDateTime).format(tenantDateFormat)}</Table.Cell>
            <Table.Cell>{survey.endDateTime && moment(survey.endDateTime).format(tenantDateFormat)}</Table.Cell>
            <Table.Cell>{survey.status}</Table.Cell>
            <Table.Cell>{survey.enabled ? 'True' : 'False'}</Table.Cell>
            <Table.Cell>
                <AccessibleDropdown
                    icon='ellipsis vertical'
                    pointing='top right'
                    id={survey.id}
                    value={false}
                    className={css['survey-dropdown']}
                    aria-label={survey.title}
                    options={surveyOptions(survey.status)}
                    selectOnBlur={false}
                    selectOnNavigation={false}
                    openOnFocus={false}
                    onChange={(e, { value }) => {
                        if(value === 'edit') editSurvey(survey.id);
                        if(value === 'changeStatus') changeSurveyStatus(survey.id, survey.status);
                        if(value === 'duplicate') duplicateTemplate(survey.id);
                    }}
                />
            </Table.Cell>
        </Table.Row>
    )

    return (
        <SortableTable
            columnToSort={sorting.sortCol}
            sortDirection={sorting.dir}
            onSortChange={onSort}
            headers={headers}
            itemName={'surveys'}
            items={sortedSurveys}
            renderItem={renderRow}
            shouldPaginate={false}
            celled
        />
    );
}

StudentSurveyListTable.propTypes = {
    editSurvey: PropTypes.func.isRequired,
    duplicateTemplate: PropTypes.func.isRequired,
    changeSurveyStatus: PropTypes.func.isRequired,
    surveys: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        startDateTime: PropTypes.string,
        endDateTime: PropTypes.string,
        status: PropTypes.string.isRequired,
        enabled: PropTypes.bool.isRequired
    })).isRequired,
    tenantDateFormat: PropTypes.string.isRequired
};

export default StudentSurveyListTable;
